import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Spinner } from '../../shared/ui/spinner';
import {
	Calendar,
	Pencil,
	Plus,
	Trash2,
	TrendingDown,
	TrendingUp,
	X,
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
	endOfDay,
	endOfMonth,
	format,
	startOfDay,
	startOfMonth,
	subDays,
	subMonths,
} from 'date-fns';
import styles from './Expenses.module.css';
import { datePickerLocale } from '../../shared/i18n/date-picker-locale';
import { useAuth } from '../../context/AuthContext';
import { useOverviewCurrency } from '../../context/OverviewCurrencyContext';
import { useOverviewRefresh } from '../../context/OverviewRefreshContext';
import { getOverviewRequest } from '../../modules/dashbord/api/index';
import expences from '../../shared/assets/icons/expences.png';
// import Filter2Search from '../../assets/icons/filter.svg';
import { Filter } from '../../shared/assets/icons';

import {
	formatDateOnlyToDDMMYYYY,
	sortTransactionsNewestFirst,
} from '../../shared/lib/date/date-utils';
import GlassMinus from '../../shared/assets/categories/GlassMinus.png';
import CashCoin from '../../shared/assets/categories/CashCoin.png';
import StudentRegular from '../../shared/assets/categories/StudentRegular.png';
import ServingFood from '../../shared/assets/categories/ServingFood.png';
import HouseLineRegular from '../../shared/assets/categories/HouseLineRegular.png';
import SecurityValidation from '../../shared/assets/categories/SecurityValidation.png';
import AddMoneyCircle from '../../shared/assets/categories/AddMoneyCircle.png';
import EmojiFunnySquare from '../../shared/assets/categories/EmojiFunnySquare.png';
import Healtcare from '../../shared/assets/categories/Healthcare.png';
import Shopping from '../../shared/assets/categories/ShoppingBag.png';
import Taxes from '../../shared/assets/categories/Taxes.png';
import ConvertRange from '../../shared/assets/categories/ScisTransparentSupply.png';
import SelfTransfer from '../../shared/assets/categories/SelfTransfer.png';
import HomeBolt from '../../shared/assets/categories/HomeBolt.png';
import Gift from '../../shared/assets/categories/Gift.png';
import AddRecordModal from '../../components/AddRecordModal/AddRecordModal';
import {
	expensesDeleteRequest,
	expensesGerCategoriesRequest,
	getExpensesRequest,
	getRecurringExpensesRequest,
	recurringExpensesDeleteRequest,
} from './api';
import { buildListParams } from '../../shared/lib/list-params/build-list-params';

const iconMap = {
	Business: CashCoin,
	Education: StudentRegular,
	Entertainment: EmojiFunnySquare,
	'Food & Dining': ServingFood,
	'Gifts & Donations': Gift,
	Healthcare: Healtcare,
	Housing: HouseLineRegular,
	Insurance: SecurityValidation,
	Other: AddMoneyCircle,
	'Personal Care': Healtcare,
	Shopping: Shopping,
	Taxes: Taxes,
	Transportation: ConvertRange,
	Travel: SelfTransfer,
	Utilities: HomeBolt,
};

// Currency symbol helper function
const getCurrencySymbol = currency => {
	const symbols = {
		USD: '$',
		EUR: '€',
		AMD: '֏',
		RUB: '₽',
	};
	return symbols[currency] || currency;
};

export const Expenses = () => {
	const intl = useIntl();
	const { userInfo } = useAuth();
	const { currency: overviewCurrency } = useOverviewCurrency();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [activeFilters, setActiveFilters] = useState(['All']);
	const [showFilterModal, setShowFilterModal] = useState(false);
	const [selectedCategories, setSelectedCategories] = useState(['All']);
	// const [selectedFrequency, setSelectedFrequency] = useState("One Time");
	const [dateRange, setDateRange] = useState({ from: null, to: null });
	const [committedCategoryNames, setCommittedCategoryNames] = useState(['All']);
	const [committedDateRange, setCommittedDateRange] = useState({
		from: null,
		to: null,
	});
	const [listFetchKey, setListFetchKey] = useState(0);
	const [categories, setCategories] = useState([]);
	const [expenses, setExpenses] = useState([]);
	const [loading, setLoading] = useState(true);
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	const [hiddenChartCategories, setHiddenChartCategories] = useState([]);
	const [editingExpense, setEditingExpense] = useState(null);
	const [deleteConfirmExpense, setDeleteConfirmExpense] = useState(null);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [viewMode, setViewMode] = useState('regular'); // "regular" | "recurring"
	const [recurringExpenses, setRecurringExpenses] = useState([]);
	const [editingRecurringExpense, setEditingRecurringExpense] = useState(null);
	const [deleteConfirmRecurringExpense, setDeleteConfirmRecurringExpense] =
		useState(null);
	const [deleteRecurringLoading, setDeleteRecurringLoading] = useState(false);
	// State for summary card
	const [userCurrency, setUserCurrency] = useState('USD');
	const [currentMonthTotal, setCurrentMonthTotal] = useState(0);
	const [monthComparison, setMonthComparison] = useState({
		percentage: null,
		trend: null,
		message: intl.formatMessage({ id: 'FinPathDashboardCartID10' }),
	});

	// Fetch overview data for summary card (use sidebar-header dropdown currency)
	useEffect(() => {
		const fetchOverviewData = async () => {
			try {
				const currency =
					overviewCurrency || userInfo?.currency_preference || 'USD';
				setUserCurrency(currency);

				// Fetch overview data for selected currency
				const overviewResponse = await getOverviewRequest(null, null, currency);

				// Calculate current month total
				const currentTotal = calculateCurrentMonthExpenses(
					overviewResponse,
					currency,
				);
				setCurrentMonthTotal(currentTotal);

				// Calculate previous month total
				const previousTotal = calculatePreviousMonthExpenses(
					overviewResponse,
					currency,
				);

				// Calculate comparison
				const comparison = calculateMonthComparison(
					currentTotal,
					previousTotal,
				);
				setMonthComparison(comparison);
			} catch (error) {
				console.error('Failed to fetch overview data:', error);
				// Set defaults on error
				setCurrentMonthTotal(0);
				setMonthComparison({
					percentage: null,
					trend: null,
					message: intl.formatMessage({ id: 'FinPathDashboardCartID10' }),
				});
			}
		};

		fetchOverviewData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [overviewCurrency, userInfo, refreshTrigger]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);
				const categoriesResponse = await expensesGerCategoriesRequest();
				const categoriesData =
					categoriesResponse?.categories ||
					categoriesResponse?.data ||
					categoriesResponse ||
					[];

				const expenseListParams = buildListParams(
					{ offset: 0, limit: 1000 },
					categoriesData,
					committedCategoryNames,
					committedDateRange,
				);

				const [expensesResponse, recurringExpensesResponse] = await Promise.all(
					[
						getExpensesRequest(expenseListParams),
						getRecurringExpensesRequest(),
					],
				);

				setCategories(categoriesData);

				const expensesData =
					expensesResponse?.expenses ||
					expensesResponse?.data ||
					expensesResponse ||
					[];
				const recurringExpensesData =
					recurringExpensesResponse?.recurring_expenses ||
					recurringExpensesResponse?.data ||
					recurringExpensesResponse ||
					[];

				setExpenses(Array.isArray(expensesData) ? expensesData : []);
				setRecurringExpenses(
					Array.isArray(recurringExpensesData) ? recurringExpensesData : [],
				);
			} catch (error) {
				console.error('Failed to load data:', error);
				setExpenses([]);
				setRecurringExpenses([]);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
		// Committed filters are read on purpose without being dependencies: the
		// list refetches when the user applies them (listFetchKey), not while the
		// filter modal is still being edited.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshTrigger, listFetchKey]);

	// Position card dropdown in viewport (for portal) so it isn't clipped
	const refreshData = useCallback(() => {
		setRefreshTrigger(prev => prev + 1);
	}, []);

	const { setRefreshCallback } = useOverviewRefresh();
	useEffect(() => {
		setRefreshCallback(refreshData);
		return () => setRefreshCallback(null);
	}, [setRefreshCallback, refreshData]);

	// Calculate current month expense total from overview data
	const calculateCurrentMonthExpenses = (overviewData, currency) => {
		if (
			!overviewData ||
			!overviewData.data ||
			!Array.isArray(overviewData.data)
		) {
			return 0;
		}

		// Get current month in YYYY-MM format
		const now = new Date();
		const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

		// Filter for current month and selected currency
		const currentMonthData = overviewData.data.find(
			item => item.currency === currency && item.month === currentMonth,
		);

		if (!currentMonthData) {
			return 0;
		}

		return Number(currentMonthData.expenses) || 0;
	};

	// Calculate previous month expense total from overview data
	const calculatePreviousMonthExpenses = (overviewData, currency) => {
		if (
			!overviewData ||
			!overviewData.data ||
			!Array.isArray(overviewData.data)
		) {
			return 0;
		}

		// Get previous month in YYYY-MM format
		const now = new Date();
		const previousMonthDate = subMonths(now, 1);
		const previousMonth = `${previousMonthDate.getFullYear()}-${String(previousMonthDate.getMonth() + 1).padStart(2, '0')}`;

		// Filter for previous month and selected currency
		const previousMonthData = overviewData.data.find(
			item => item.currency === currency && item.month === previousMonth,
		);

		if (!previousMonthData) {
			return 0;
		}

		return Number(previousMonthData.expenses) || 0;
	};

	// Calculate month-over-month comparison
	const calculateMonthComparison = (current, previous) => {
		if (previous === 0 || previous === null || previous === undefined) {
			return {
				percentage: null,
				trend: null,
				message: intl.formatMessage({ id: 'FinPathDashboardCartID10' }),
			};
		}

		const percentage = ((current - previous) / previous) * 100;
		const trend = percentage >= 0 ? 'up' : 'down';
		const absPercentage = Math.abs(percentage).toFixed(1);

		return {
			percentage: absPercentage,
			trend,
			message: `${absPercentage}% ${intl.formatMessage({ id: 'FinPathDashboardCartID11' })}`,
		};
	};

	const handleEditExpense = expense => {
		setEditingExpense(expense);
		setIsModalOpen(true);
	};

	const handleDeleteExpense = expenseId => {
		const expenseToDelete = expenses.find(exp => exp.id === expenseId);
		if (expenseToDelete) {
			setDeleteConfirmExpense(expenseToDelete);
		}
	};

	const handleConfirmDelete = async () => {
		if (!deleteConfirmExpense) return;

		setDeleteLoading(true);
		try {
			await expensesDeleteRequest(deleteConfirmExpense.id);
			setExpenses(expenses.filter(exp => exp.id !== deleteConfirmExpense.id));
			setDeleteConfirmExpense(null);
			refreshData();
		} catch (err) {
			console.error('Error deleting expense:', err);
			alert(err.message || 'Failed to delete expense');
		} finally {
			setDeleteLoading(false);
		}
	};

	const handleCancelDelete = () => {
		setDeleteConfirmExpense(null);
	};

	const handleEditRecurringExpense = recurringExpense => {
		setEditingRecurringExpense(recurringExpense);
		setIsModalOpen(true);
	};

	const handleDeleteRecurringExpense = recurringExpenseId => {
		const recurringExpenseToDelete = recurringExpenses.find(
			exp => exp.id === recurringExpenseId,
		);
		if (recurringExpenseToDelete) {
			setDeleteConfirmRecurringExpense(recurringExpenseToDelete);
		}
	};

	const handleConfirmDeleteRecurring = async () => {
		if (!deleteConfirmRecurringExpense) return;

		setDeleteRecurringLoading(true);
		try {
			await recurringExpensesDeleteRequest(deleteConfirmRecurringExpense.id);
			setRecurringExpenses(
				recurringExpenses.filter(
					exp => exp.id !== deleteConfirmRecurringExpense.id,
				),
			);
			setDeleteConfirmRecurringExpense(null);
			refreshData();
		} catch (err) {
			console.error('Error deleting recurring expense:', err);
			alert(err.message || 'Failed to delete recurring expense');
		} finally {
			setDeleteRecurringLoading(false);
		}
	};

	const handleCancelDeleteRecurring = () => {
		setDeleteConfirmRecurringExpense(null);
	};

	// Use appropriate data source based on view mode; only amounts in sidebar-header currency for chart/sum
	const dataSource = viewMode === 'recurring' ? recurringExpenses : expenses;
	const selectedCurrency =
		overviewCurrency || userInfo?.currency_preference || 'USD';
	const dataSourceInCurrency = dataSource.filter(
		exp => (exp.currency || 'USD') === selectedCurrency,
	);

	const expenseData = categories
		.map(category => {
			const categoryExpenses = dataSourceInCurrency.filter(
				exp => exp.category_id === category.id,
			);
			const totalAmount = categoryExpenses.reduce(
				(sum, exp) => sum + (Number(exp.amount) || 0),
				0,
			);

			if (totalAmount > 0) {
				const iconLookupName = category.original_name || category.name;
				const colorLookupName = category.original_name || category.name;
				return {
					id: category.id,
					name: category.name,
					value: totalAmount,
					displayValue: totalAmount,
					icon: iconMap[iconLookupName],
					color: getColorForCategory(colorLookupName),
				};
			}
			return null;
		})
		.filter(Boolean);

	const transactions = expenses
		.map(expense => {
			const category = categories.find(cat => cat.id === expense.category_id);
			if (expense.amount > 0) {
				const iconLookupName = category?.original_name || category?.name;
				return {
					id: expense.id,
					category: category?.name || 'Other',
					date: formatDateOnlyToDDMMYYYY(expense.date),
					transactionDate: expense.date,
					createdAt: expense.created_at,
					amount: expense.amount,
					icon: iconMap[iconLookupName] || AddMoneyCircle,
					currency: expense.currency,
				};
			}
			return null;
		})
		.filter(Boolean);

	// Prepare recurring transactions
	const recurringTransactions = recurringExpenses
		.map(recurringExpense => {
			const category = categories.find(
				cat => cat.id === recurringExpense.category_id,
			);
			const amount = Number(recurringExpense.amount) || 0;
			if (amount > 0) {
				const recurringPeriod =
					recurringExpense.recurring_period === 'weekly'
						? intl.formatMessage({ id: 'FrequencyID1' })
						: recurringExpense.recurring_period === 'monthly'
							? intl.formatMessage({ id: 'FrequencyD2' })
							: recurringExpense.recurring_period ||
								intl.formatMessage({ id: 'FrequencyD2' });
				const iconLookupName = category?.original_name || category?.name;
				return {
					id: recurringExpense.id,
					category: category?.name || 'Other',
					date: formatDateOnlyToDDMMYYYY(recurringExpense.start_date),
					transactionDate: recurringExpense.start_date,
					createdAt: recurringExpense.created_at,
					frequency: recurringPeriod,
					amount: amount,
					icon: iconMap[iconLookupName] || AddMoneyCircle,
					currency: recurringExpense.currency,
					isRecurring: true,
				};
			}
			return null;
		})
		.filter(Boolean);

	const sortedTransactions = sortTransactionsNewestFirst(transactions);
	const sortedRecurringTransactions = sortTransactionsNewestFirst(
		recurringTransactions,
	);

	const validatedChartData = expenseData.map(item => ({
		...item,
		value: Number(item.value) || 0,
		displayValue: Number(item.displayValue) || Number(item.value) || 0,
	}));

	// Handle legend click to hide/show chart segment
	const handleLegendClick = categoryName => {
		setHiddenChartCategories(prev => {
			if (prev.includes(categoryName)) {
				return prev.filter(cat => cat !== categoryName);
			} else {
				return [...prev, categoryName];
			}
		});
	};

	// Filter chart data based on hidden categories
	const getFilteredChartData = () => {
		const baseFilteredData = committedCategoryNames.includes('All')
			? validatedChartData
			: validatedChartData.filter(item =>
					committedCategoryNames.includes(item.name),
				);

		// Filter out hidden categories
		return baseFilteredData.filter(
			item => !hiddenChartCategories.includes(item.name),
		);
	};

	const currentChartData = getFilteredChartData();

	const removeFilter = filter => {
		if (filter === 'All') {
			setSelectedCategories(['All']);
			// setSelectedFrequency("One Time");
			setDateRange({ from: null, to: null });
			setActiveFilters(['All']);
			setHiddenChartCategories([]);
			setCommittedCategoryNames(['All']);
			setCommittedDateRange({ from: null, to: null });
			setListFetchKey(k => k + 1);
			return;
		}

		const newActiveFilters = activeFilters.filter(f => f !== filter);
		let nextCategories = [...selectedCategories];
		let nextDateRange = { ...dateRange };

		const allCategoryNames = categories.map(cat => cat.name);
		if (allCategoryNames.includes(filter)) {
			nextCategories = selectedCategories.filter(cat => cat !== filter);
			if (nextCategories.length === 0) {
				nextCategories = ['All'];
			}
		}

		if (
			filter.includes('→') ||
			/^\d{2}\.\d{2}\.\d{2}$/.test(String(filter).trim())
		) {
			nextDateRange = { from: null, to: null };
		}

		setSelectedCategories(nextCategories);
		setDateRange(nextDateRange);
		setActiveFilters(newActiveFilters.length > 0 ? newActiveFilters : ['All']);
		setCommittedCategoryNames(nextCategories);
		setCommittedDateRange(nextDateRange);
		setListFetchKey(k => k + 1);
	};

	const handleCategoryToggle = category => {
		if (category === 'All') {
			if (selectedCategories.includes('All')) {
				setSelectedCategories([]);
				setHiddenChartCategories([]);
			} else {
				const allCategoryNames = categories.map(cat => cat.name);
				setSelectedCategories(['All', ...allCategoryNames]);
			}
		} else {
			setSelectedCategories(prev => {
				const newCategories = prev.includes(category)
					? prev.filter(cat => cat !== category && cat !== 'All')
					: [...prev.filter(cat => cat !== 'All'), category];

				const allCategoryNames = categories.map(cat => cat.name);
				const allSelected = allCategoryNames.every(cat =>
					newCategories.includes(cat),
				);

				if (allSelected) {
					return ['All', ...allCategoryNames];
				}

				return newCategories;
			});
		}
	};
	const handleClearFilters = () => {
		setSelectedCategories(['All']);
		// setSelectedFrequency("One Time");
		setDateRange({ from: null, to: null });
		setActiveFilters(['All']);
		setHiddenChartCategories([]);
		setCommittedCategoryNames(['All']);
		setCommittedDateRange({ from: null, to: null });
		setListFetchKey(k => k + 1);
	};

	const handleApplyFilters = () => {
		const newFilters = [];

		// Add category filters
		if (selectedCategories.includes('All')) {
			newFilters.push('All');
		} else if (selectedCategories.length > 0) {
			const selectedCats = selectedCategories.filter(cat => cat !== 'All');
			newFilters.push(...selectedCats);
		}

		// Add frequency filter
		// if (selectedFrequency && selectedFrequency !== "Monthly") {
		//   newFilters.push(selectedFrequency);
		// }

		// Add date range filter
		if (dateRange.from && dateRange.to) {
			const fromStr = format(dateRange.from, 'dd.MM.yy');
			const toStr = format(dateRange.to, 'dd.MM.yy');
			if (fromStr === toStr) {
				newFilters.push(fromStr);
			} else {
				newFilters.push(`${fromStr} → ${toStr}`);
			}
		}

		setActiveFilters(newFilters.length > 0 ? newFilters : ['All']);
		setCommittedCategoryNames([...selectedCategories]);
		setCommittedDateRange({
			from: dateRange.from,
			to: dateRange.to,
		});
		setListFetchKey(k => k + 1);
		setShowFilterModal(false);
	};

	const handleResetFilters = () => {
		setSelectedCategories(['All']);
		// setSelectedFrequency("One Time");
		setDateRange({ from: null, to: null });
		setActiveFilters(['All']);
		setHiddenChartCategories([]);
		setCommittedCategoryNames(['All']);
		setCommittedDateRange({ from: null, to: null });
		setListFetchKey(k => k + 1);
	};

	const handleDatePreset = preset => {
		const today = new Date();
		let fromDate = null;
		let toDate = today;

		switch (preset) {
			case 'today':
				fromDate = today;
				toDate = today;
				break;
			case 'last7days':
				fromDate = subDays(today, 7);
				break;
			case 'last30days':
				fromDate = subDays(today, 30);
				break;
			case 'thisMonth':
				fromDate = startOfMonth(today);
				toDate = endOfMonth(today);
				break;
			case 'lastMonth':
				const lastMonth = subMonths(today, 1);
				fromDate = startOfMonth(lastMonth);
				toDate = endOfMonth(lastMonth);
				break;
			default:
				break;
		}

		setDateRange({ from: fromDate, to: toDate });
	};

	// Get the appropriate transactions list based on view mode
	const transactionsToFilter =
		viewMode === 'recurring' ? sortedRecurringTransactions : sortedTransactions;

	const filteredTransactions = transactionsToFilter.filter(transaction => {
		if (viewMode !== 'recurring') {
			return true;
		}

		if (
			!selectedCategories.includes('All') &&
			!selectedCategories.includes(transaction.category)
		) {
			return false;
		}

		if (dateRange.from || dateRange.to) {
			const parts = transaction.date.split('.');
			const transactionDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);

			const fromDate = dateRange.from
				? startOfDay(new Date(dateRange.from))
				: null;
			const toDate = dateRange.to ? endOfDay(new Date(dateRange.to)) : null;

			const dateMatch =
				(!fromDate || transactionDate >= fromDate) &&
				(!toDate || transactionDate <= toDate);

			if (!dateMatch) {
				return false;
			}
		}

		return true;
	});

	const categoryData = [
		{ name: 'All', icon: null },
		...categories
			.map(category => {
				const iconLookupName = category.original_name || category.name;
				return {
					name: category.name,
					icon: iconMap[iconLookupName],
				};
			})
			.sort((a, b) => a.name.localeCompare(b.name)),
	];

	/** Edit + delete buttons, shown on every table row and mobile card. */
	const renderRowActions = transaction => {
		const record =
			viewMode === 'recurring'
				? recurringExpenses.find(exp => exp.id === transaction.id)
				: expenses.find(exp => exp.id === transaction.id);

		return (
			<>
				<button
					type='button'
					className={styles.iconButton}
					disabled={!record}
					title={intl.formatMessage({ id: 'ExpensesID30' })}
					aria-label={intl.formatMessage({ id: 'ExpensesID30' })}
					onClick={() =>
						viewMode === 'recurring'
							? handleEditRecurringExpense(record)
							: handleEditExpense(record)
					}
				>
					<Pencil size={18} />
				</button>
				<button
					type='button'
					className={`${styles.iconButton} ${styles.deleteButton}`}
					title={intl.formatMessage({ id: 'ExpensesID31' })}
					aria-label={intl.formatMessage({ id: 'ExpensesID31' })}
					onClick={() =>
						viewMode === 'recurring'
							? handleDeleteRecurringExpense(transaction.id)
							: handleDeleteExpense(transaction.id)
					}
				>
					<Trash2 size={18} />
				</button>
			</>
		);
	};

	const CustomTooltip = ({ active, payload }) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;
			return (
				<div className={styles.customTooltip}>
					<p className={styles.tooltipLabel}>{data.name}</p>
					<p className={styles.tooltipValue}>
						{(data.displayValue || data.value).toLocaleString()}
						{getCurrencySymbol(userCurrency)}
					</p>
				</div>
			);
		}
		return null;
	};

	if (loading) {
		return <Spinner />;
	}

	return (
		<div className={styles.container}>
			{/* <SidebarHeader /> */}
			<div className={styles.content}>
				<div className={styles.topSection}>
					<div className={styles.summaryCard}>
						<h2 className={styles.summaryTitle}>
							{intl.formatMessage({ id: 'ExpensesID5' })}
						</h2>
						<span className={styles.summarySubtitle}>
							{intl.formatMessage({ id: 'ExpensesID6' })}
						</span>
						<div className={styles.coinIcon}>
							<img src={expences} alt='expences' />
						</div>
						<div className={styles.summaryAmount}>
							<span>
								{currentMonthTotal.toLocaleString()}
								{getCurrencySymbol(userCurrency)}
							</span>
							{monthComparison.trend === 'up' ? (
								<TrendingUp className={styles.trendIcon} size={16} />
							) : monthComparison.trend === 'down' ? (
								<TrendingDown className={styles.trendIcon} size={16} />
							) : null}
						</div>
						<div className={styles.summaryChange}>
							<span className={styles.paragrap}>{monthComparison.message}</span>
						</div>
					</div>

					<div className={styles.chartContainer}>
						{filteredTransactions.length > 0 ? (
							<div className={styles.chartWrapper}>
								<div className={styles.chartBox}>
									<ResponsiveContainer width='100%' height='100%'>
										<PieChart>
											<Pie
												data={currentChartData}
												cx='50%'
												cy='50%'
												innerRadius='56%'
												outerRadius='80%'
												paddingAngle={1}
												dataKey='value'
												minAngle={5}
												isAnimationActive={false}
											>
												{currentChartData.map((entry, index) => (
													<Cell key={`cell-${index}`} fill={entry.color} />
												))}
											</Pie>
											<Tooltip
												content={<CustomTooltip />}
												animationDuration={0}
											/>
										</PieChart>
									</ResponsiveContainer>
								</div>

								<div className={styles.legendContainer}>
									{validatedChartData
										.filter(item =>
											selectedCategories.includes('All')
												? true
												: selectedCategories.includes(item.name),
										)
										.map((item, index) => {
											const isHidden = hiddenChartCategories.includes(
												item.name,
											);
											return (
												<div
													key={index}
													className={styles.legendRow}
													onClick={() => handleLegendClick(item.name)}
													style={{ cursor: 'pointer' }}
												>
													<div
														className={styles.legendColor}
														style={{
															backgroundColor: isHidden
																? '#e0e0e0'
																: item.color,
															opacity: isHidden ? 0.5 : 1,
															position: 'relative',
														}}
													>
														{isHidden && (
															<div className={styles.hiddenIndicator}>×</div>
														)}
													</div>
													<span
														className={`${styles.legendName} ${
															isHidden ? styles.legendNameHidden : ''
														}`}
													>
														{item.name}
													</span>
												</div>
											);
										})}
								</div>
							</div>
						) : (
							<div className={styles.chartWrapper}>
								<div className={styles.chartBox}>
									<div
										className={styles.ghostDonut}
										role='img'
										aria-label={intl.formatMessage({ id: 'ExpensesID7' })}
									/>
								</div>
							</div>
						)}
					</div>
				</div>

				<div className={styles.filtersSection}>
					<div className={styles.filterControls}>
						<button
							className={`${styles.viewModeToggle} ${
								viewMode === 'regular' ? styles.viewModeActive : ''
							}`}
							onClick={() => setViewMode('regular')}
						>
							{intl.formatMessage({ id: 'ExpensesID8' })}
						</button>
						<button
							className={`${styles.viewModeToggle} ${
								viewMode === 'recurring' ? styles.viewModeActive : ''
							}`}
							onClick={() => setViewMode('recurring')}
						>
							{intl.formatMessage({ id: 'ExpensesID9' })}
						</button>
						<button
							className={styles.filterButton}
							onClick={() => setShowFilterModal(true)}
						>
							<Filter width={20} height={20} />
							<span>{intl.formatMessage({ id: 'ExpensesID10' })}</span>
						</button>
						{activeFilters
							.filter(filter => filter !== 'All')
							.map(filter => (
								<button key={filter} className={styles.filterTag}>
									<span>{filter}</span>
									<X size={16} onClick={() => removeFilter(filter)} />
								</button>
							))}
					</div>
					<button
						className={styles.addButton}
						onClick={() => setIsModalOpen(true)}
					>
						<span>{intl.formatMessage({ id: 'ExpensesID11' })}</span>
						<Plus size={20} />
					</button>
				</div>

				{showFilterModal && (
					<div className={styles.modalOverlay}>
						<div className={styles.modalContent}>
							<div className={styles.modalHeader}>
								<h3>{intl.formatMessage({ id: 'ExpensesID12' })}</h3>
								<button
									className={styles.closeButton}
									onClick={() => setShowFilterModal(false)}
								>
									<X size={20} />
								</button>
							</div>

							<div className={styles.filterSection}>
								<div className={styles.dateSelector}>
									<h4 className={styles.sectionTitle}>
										{intl.formatMessage({ id: 'ExpensesID13' })}
									</h4>

									<div className={styles.dateInputs}>
										<div className={styles.dateInputGroup}>
											<label>
												{intl.formatMessage({ id: 'ExpensesID14' })}
											</label>
											<div className={styles.dateInputWrapper}>
												<DatePicker
													selected={dateRange.from}
													onChange={date => {
														if (dateRange.to && date > dateRange.to) {
															setDateRange({ from: date, to: date });
														} else {
															setDateRange(prev => ({
																...prev,
																from: date,
															}));
														}
													}}
													selectsStart
													startDate={dateRange.from}
													endDate={dateRange.to}
													dateFormat='dd.MM.yy'
													placeholderText={intl.formatMessage({
														id: 'DatePlaceholderId1',
													})}
													locale={datePickerLocale(intl.locale)}
													className={styles.datePickerInput}
													isClearable
													showMonthDropdown
													showYearDropdown
													dropdownMode='scroll'
													scrollableYearDropdown
													yearDropdownItemNumber={15}
													popperPlacement='bottom-start'
													calendarStartDay={1}
												/>
												<Calendar size={16} className={styles.calendarIcon} />
											</div>
										</div>
										<div className={styles.dateInputGroup}>
											<label>
												{intl.formatMessage({ id: 'ExpensesID15' })}
											</label>
											<div className={styles.dateInputWrapper}>
												<DatePicker
													selected={dateRange.to}
													onChange={date => {
														if (dateRange.from && date < dateRange.from) {
															setDateRange({ from: date, to: date });
														} else {
															setDateRange(prev => ({
																...prev,
																to: date,
															}));
														}
													}}
													selectsEnd
													startDate={dateRange.from}
													endDate={dateRange.to}
													minDate={dateRange.from}
													dateFormat='dd.MM.yy'
													placeholderText={intl.formatMessage({
														id: 'DatePlaceholderId1',
													})}
													locale={datePickerLocale(intl.locale)}
													className={styles.datePickerInput}
													isClearable
													showMonthDropdown
													showYearDropdown
													dropdownMode='scroll'
													scrollableYearDropdown
													yearDropdownItemNumber={15}
													popperPlacement='bottom-end'
													calendarStartDay={1}
												/>
												<Calendar size={16} className={styles.calendarIcon} />
											</div>
										</div>
									</div>

									<div className={styles.datePresetsSection}>
										<div className={styles.datePresets}>
											<button
												type='button'
												className={styles.datePreset}
												onClick={() => handleDatePreset('last30days')}
											>
												{intl.formatMessage({ id: 'ExpensesID16' })}
											</button>
											<button
												type='button'
												className={styles.datePreset}
												onClick={() => handleDatePreset('last7days')}
											>
												{intl.formatMessage({ id: 'ExpensesID17' })}
											</button>
											<button
												type='button'
												className={styles.datePreset}
												onClick={() => handleDatePreset('today')}
											>
												{intl.formatMessage({ id: 'ExpensesID18' })}
											</button>
										</div>
									</div>
								</div>
							</div>

							<div className={styles.filterSection}>
								<h4 className={styles.sectionTitle}>
									{intl.formatMessage({ id: 'ExpensesID19' })}
								</h4>
								<div className={styles.categoryList}>
									{categoryData.map(category => {
										const isChecked =
											category.name === 'All'
												? selectedCategories.includes('All')
												: selectedCategories.includes(category.name) ||
													selectedCategories.includes('All');

										return (
											<label
												key={category.name}
												className={styles.categoryItem}
											>
												{category.icon && (
													<img src={category.icon} alt={category.name} />
												)}
												{/* {!category.icon && (
                            <div className={styles.iconPlaceholder}></div>
                          )} */}
												<span className={styles.categoryLabel}>
													{category.name}
												</span>

												<div className={styles.checkboxWrapper}>
													<input
														type='checkbox'
														checked={isChecked}
														onChange={() => handleCategoryToggle(category.name)}
														className={styles.checkbox}
													/>
													<span className={styles.checkmark}></span>
												</div>
											</label>
										);
									})}
								</div>
							</div>

							<div className={styles.modalActions}>
								<button
									className={styles.resetButton}
									onClick={handleResetFilters}
								>
									{intl.formatMessage({ id: 'ExpensesID20' })}
								</button>
								<button
									className={styles.applyButton}
									onClick={handleApplyFilters}
								>
									{intl.formatMessage({ id: 'ExpensesID21' })}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Mobile: card list */}
				<div className={styles.cardsContainer}>
					{filteredTransactions.length > 0 ? (
						filteredTransactions.map((transaction, index) => {
							return (
								<div
									key={`card-${transaction.id}-${index}`}
									className={styles.expenseCard}
								>
									<div className={styles.cardTopRow}>
										<span className={styles.cardDate}>{transaction.date}</span>
										<div className={styles.actionsCell}>
											{renderRowActions(transaction)}
										</div>
									</div>
									<div className={styles.cardMainRow}>
										<div className={styles.cardCategory}>
											<img
												src={transaction.icon}
												alt={transaction.category}
												className={styles.cardCategoryIcon}
											/>
											<span>{transaction.category}</span>
										</div>
										<span className={styles.cardAmount}>
											{Number(transaction.amount) < 0
												? transaction.amount
												: `-${transaction.amount}`}{' '}
											{transaction.currency}
										</span>
									</div>
								</div>
							);
						})
					) : (
						<div className={styles.emptyTableState}>
							<h2 className={styles.emptyTableTitle}>
								{intl.formatMessage({ id: 'ExpensesID22' })}
							</h2>
							<img
								className={styles.emptyTableImage}
								src={GlassMinus}
								alt='GlassMinus'
							/>
							<p className={styles.emptyTableMessage}>
								{intl.formatMessage({ id: 'ExpensesID23' })}
								<br />
								{intl.formatMessage({ id: 'ExpensesID24' })}
							</p>
							<button
								className={styles.clearFiltersButton}
								onClick={handleClearFilters}
							>
								{intl.formatMessage({ id: 'ExpensesID25' })}
							</button>
						</div>
					)}
				</div>

				{/* Desktop: table */}
				<div className={styles.tableContainer}>
					<div
						className={`${styles.tableHeader} ${
							viewMode === 'recurring' ? styles.tableHeaderRecurring : ''
						}`}
					>
						<div className={styles.tableHeaderCell}>
							{intl.formatMessage({ id: 'ExpensesID26' })}
						</div>
						<div className={styles.tableHeaderCell}>
							{viewMode === 'recurring'
								? intl.formatMessage({ id: 'IncomeID26' })
								: intl.formatMessage({ id: 'IncomeID27' })}
						</div>
						{viewMode === 'recurring' && (
							<div className={styles.tableHeaderCell}>
								{intl.formatMessage({ id: 'ExpensesID27' })}
							</div>
						)}
						<div className={styles.tableHeaderCell}>
							{intl.formatMessage({ id: 'ExpensesID28' })}
						</div>
						<div className={styles.tableHeaderCell}>
							{intl.formatMessage({ id: 'ExpensesID29' })}
						</div>
					</div>
					<div className={styles.tableBody}>
						{filteredTransactions.length > 0 ? (
							filteredTransactions.map((transaction, index) => {
								return (
									<div
										key={`${transaction.id}-${index}`}
										className={`${styles.tableRow} ${
											viewMode === 'recurring' ? styles.tableRowRecurring : ''
										}`}
									>
										<div className={styles.tableCell}>
											<img
												src={transaction.icon}
												alt={transaction.category}
												className={styles.categoryIcon}
											/>
											<span>{transaction.category}</span>
										</div>
										{viewMode === 'recurring' ? (
											<>
												<div className={styles.tableCell}>
													{transaction.frequency || 'Monthly'}
												</div>
												<div className={styles.tableCell}>
													{transaction.date}
												</div>
											</>
										) : (
											<div className={styles.tableCell}>{transaction.date}</div>
										)}
										<div className={styles.tableCell}>
											{transaction.amount} {transaction.currency}
										</div>
										<div
											className={`${styles.tableCell} ${styles.actionsCell}`}
										>
											{renderRowActions(transaction)}
										</div>
									</div>
								);
							})
						) : (
							<div className={styles.emptyTableState}>
								<h2 className={styles.emptyTableTitle}>
									{intl.formatMessage({ id: 'ExpensesID32' })}
								</h2>
								<img
									className={styles.emptyTableImage}
									src={GlassMinus}
									alt='GlassMinus'
								/>
								<p className={styles.emptyTableMessage}>
									{intl.formatMessage({ id: 'ExpensesID33' })}
									<br />
									{intl.formatMessage({ id: 'ExpensesID34' })}
								</p>
								<button
									className={styles.clearFiltersButton}
									onClick={handleClearFilters}
								>
									{intl.formatMessage({ id: 'ExpensesID35' })}
								</button>
							</div>
						)}
					</div>
				</div>
			</div>

			<AddRecordModal
				open={isModalOpen}
				onClose={() => {
					setIsModalOpen(false);
					setEditingExpense(null);
					setEditingRecurringExpense(null);
				}}
				onSuccess={() => refreshData()}
				defaultTab='expense'
				editingExpense={editingExpense || editingRecurringExpense}
			/>
			{deleteConfirmExpense && (
				<div className={styles.modalOverlay}>
					<div className={styles.modalContent}>
						<div className={styles.modalHeader}>
							<h3>{intl.formatMessage({ id: 'ExpensesID38' })}</h3>
						</div>
						<p className={styles.deleteConfirmText}>
							{intl.formatMessage({ id: 'ExpensesID39' })}
						</p>
						<div className={styles.modalActions}>
							<button
								className={styles.deleteConfirmButton}
								onClick={handleConfirmDelete}
								disabled={deleteLoading}
							>
								{deleteLoading
									? intl.formatMessage({ id: 'ExpensesID40' })
									: intl.formatMessage({ id: 'ExpensesID41' })}
							</button>
							<button
								className={styles.cancelConfirmButton}
								onClick={handleCancelDelete}
								disabled={deleteLoading}
							>
								{intl.formatMessage({ id: 'ExpensesID42' })}
							</button>
						</div>
					</div>
				</div>
			)}
			{deleteConfirmRecurringExpense && (
				<div className={styles.modalOverlay}>
					<div className={styles.modalContent}>
						<div className={styles.modalHeader}>
							<h3>{intl.formatMessage({ id: 'ExpensesID43' })}</h3>
						</div>
						<p className={styles.deleteConfirmText}>
							{intl.formatMessage({ id: 'ExpensesID44' })}
						</p>
						<div className={styles.modalActions}>
							<button
								className={styles.deleteConfirmButton}
								onClick={handleConfirmDeleteRecurring}
								disabled={deleteRecurringLoading}
							>
								{deleteRecurringLoading
									? intl.formatMessage({ id: 'ExpensesID45' })
									: intl.formatMessage({ id: 'ExpensesID46' })}
							</button>
							<button
								className={styles.cancelConfirmButton}
								onClick={handleCancelDeleteRecurring}
								disabled={deleteRecurringLoading}
							>
								{intl.formatMessage({ id: 'ExpensesID47' })}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

function getColorForCategory(categoryName) {
	const colors = {
		Shopping: '#b3dfcc',
		Taxes: '#22336e',
		Transportation: '#3a55ad',
		Travel: '#4969d0',
		Utilities: '#6e8ff7',
		Healthcare: '#95afff',
		'Gifts & Donations': '#a7bdff',
		Education: '#a40000',
		'Food & Dining': '#d81f1f',
		Housing: '#f23a3a',
		Insurance: '#fe7373',
		'Personal Care': '#79d3ac',
		Entertainment: '#52b289',
		Other: '#ffb4b4',
		Business: '#277e59',
	};

	return colors[categoryName] || '#cccccc';
}
